import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { apiClient } from "@/config/api";
import {
  ListUsersRequest,
  ListUsersResponse,
  UpdateAdminRequest,
  UpdateHomeownerRequest,
  UpdateSuperAdminRequest,
} from "./types";
import { RootState } from "@/types/store";

export const getUsers = createAsyncThunk(
  "admin/users",
  async (body: ListUsersRequest, { getState, rejectWithValue }) => {
    try {
      const userType = (getState() as RootState).Auth.userType;
      const queryParams: Record<string, string | undefined> = { ...body };

      if (userType === "ADMIN") {
        // Admin always gets HOMEOWNER type
        queryParams.type = "homeowner";
      } else if (!body.type) {
        // Super admin with no type specified - remove type from request
        delete queryParams.type;
      }

      // Remove any undefined or null values from query params
      const cleanParams = Object.fromEntries(
        Object.entries(queryParams).filter(([, value]) => value != null)
      );

      const response = await apiClient.get<ListUsersResponse>("/admin/users", {
        params: cleanParams,
      });

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get users failed"
        );
      }
      return rejectWithValue("Get users failed");
    }
  }
);

export const updateHomeowner = createAsyncThunk(
  "admin/homeowner/:id",
  async (body: UpdateHomeownerRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/admin/homeowner/${body.id}`,
        body.body
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update homeowner failed"
        );
      }
      return rejectWithValue("Update homeowner failed");
    }
  }
);

export const updateSuperAdmin = createAsyncThunk(
  "admin/super-admin/:id",
  async (body: UpdateSuperAdminRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/admin/super-admin/${body.id}`,
        body.body
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update super-admin failed"
        );
      }
      return rejectWithValue("Update super-admin failed");
    }
  }
);

export const updateAdmin = createAsyncThunk(
  "admin/admin/:id",
  async (body: UpdateAdminRequest, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(
        `/admin/admin/${body.id}`,
        body.body
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update admin failed"
        );
      }
      return rejectWithValue("Update admin failed");
    }
  }
);

export const deactivateUserAccount = createAsyncThunk(
  "admin/user/:id/deactivate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`admin/user/${id}/deactivate`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Request deactivation failed"
        );
      }
      return rejectWithValue("Request deactivation failed");
    }
  }
);

export const reactivateUserAccount = createAsyncThunk(
  "admin/user/:id/reactivate",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`admin/user/${id}/reactivate`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Request reactivation failed"
        );
      }
      return rejectWithValue("Request reactivation failed");
    }
  }
);

export const getUserById = createAsyncThunk(
  "admin/user/:id",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`admin/user/${id}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get user failed"
        );
      }
      return rejectWithValue("Get user failed");
    }
  }
);

export const resendInvite = createAsyncThunk(
  "admin/user/:id/resend-invite",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`admin/user/${id}/resend-invite`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Resend invite failed"
        );
      }
      return rejectWithValue("Resend invite failed");
    }
  }
);
